/**
 * `wayai knowledge refresh-preview`: the knowledge base's preview made to hold its production
 * base's files (#5142 step 4; K10, K18; #5083 decisions 112 and 145).
 *
 * `wayai knowledge migrate` writes files in production only (O3), and a preview is cloned with its
 * config, never its files, so a preview holds production's knowledge only once this runs: after
 * the preview is created, and whenever an operator wants it current again (K10). Nothing runs it on
 * its own — not migrate, and not a hub preview's creation, since every hub preview shares the one
 * preview base and a copy would overwrite the others' edits.
 *
 * It pages the data product's preview file snapshot (`POST /v1/{preview}/files/snapshot`, through
 * the `/api/data` proxy) to its end. Each page makes one range of the preview's files equal
 * production's, removing the preview's own files production does not hold, and the run prints
 * what each did. Every file type production holds is covered, and the preview must hold each one
 * too (the data product refuses the run otherwise). A re-run is safe: a page that finds the
 * preview equal writes, and costs, nothing.
 */

import {
  FILE_SNAPSHOT_CONFLICT_STATUS,
  fileSnapshotChanged,
  runFileSnapshot,
  type FileSnapshotRun,
} from '@wayai/core/contracts';
import { ApiError } from '../lib/api-client.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { resolveBases, type KnowledgeBase } from './migrate.js';

/** The data side a refresh reads: the knowledge base, its preview, and one snapshot page at a time. */
export interface PreviewKnowledgeBase extends Pick<KnowledgeBase, 'getBase' | 'listPreviews'> {
  /** One snapshot page's parsed body (`undefined` = the first page). */
  snapshotPage(preview: string, cursor: string | undefined): Promise<unknown>;
}

export interface RefreshOptions {
  orgId: string;
  base: string;
  yes: boolean;
}

export interface RefreshIo {
  log: (line: string) => void;
  /** Asked before the first page; false cancels. */
  confirm: (question: string) => Promise<boolean>;
}

/** Run one refresh. Returns what it did, or null when cancelled. */
export async function refreshPreviewKnowledge(
  kb: PreviewKnowledgeBase,
  opts: RefreshOptions,
  io: RefreshIo,
): Promise<FileSnapshotRun | null> {
  const { production, preview } = await resolveBases(kb, opts.base, opts.orgId);
  const question =
    `Make the files of preview "${sanitizeTerminalText(preview)}" equal production "${sanitizeTerminalText(production)}"'s? ` +
    'Files the preview changed are replaced, and files only it holds are removed.';
  if (!opts.yes && !(await io.confirm(question))) {
    io.log('Cancelled.');
    return null;
  }

  io.log(`Copying the files of production base "${sanitizeTerminalText(production)}" into its preview "${sanitizeTerminalText(preview)}".`);
  const run = await runFileSnapshot({
    sendPage: (cursor) => kb.snapshotPage(preview, cursor),
    // The data product refuses a page whose preview file changed while it was being copied, and
    // asks for the same cursor again.
    isConflict: (error) => error instanceof ApiError && error.status === FILE_SNAPSHOT_CONFLICT_STATUS,
    onPage: (page, n) => io.log(`  Page ${n}: ${page.written} written, ${page.removed} removed, ${page.unchanged} unchanged.`),
  });
  io.log(
    !fileSnapshotChanged(run)
      ? `Nothing changed in ${run.pages} page(s): the preview's ${run.unchanged} file(s) already equal production's.`
      : `Done in ${run.pages} page(s): ${run.written} written, ${run.removed} removed, ${run.unchanged} unchanged. ` +
          "The preview's files now equal production's.",
  );
  return run;
}
